import React, { useState, useEffect } from 'react';
import { taskDataService } from '../../../services/taskDataService';
import {
  Box,
  Typography,
  Paper,
  styled,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Tooltip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { format, parseISO, isAfter } from 'date-fns';
import { tr } from 'date-fns/locale';

// İkonlar
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import CloseIcon from '@mui/icons-material/Close';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';

// Stil tanımlamaları
const TaskContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    transition: 'box-shadow 0.3s ease'
  }
}));

const TaskCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  }
}));

const TaskCardContent = styled(CardContent)({
  flexGrow: 1,
});

const StatusChip = styled(Chip)<{ customcolor?: string }>(({ theme, customcolor }) => ({
  fontWeight: 'bold',
  backgroundColor: customcolor ? `${customcolor}20` : undefined,
  color: customcolor,
  borderColor: customcolor ? `${customcolor}50` : undefined,
  '& .MuiChip-icon': {
    color: 'inherit'
  }
}));

const ViewToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: theme.spacing(2),
}));

interface Task {
  id: string;
  name: string;
  description: string;
  status: string;
  completionType: string;
  createdAt: number;
  deadline?: string;
  isRecurring: boolean;
  dailyRepetitions?: number;
  repetitionTimes?: string[];
  startTolerance?: number; // Yeni eklenen tolerans süresi
}

const DailyTasksComponent: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // ✅ userDetails AuthContext cache'inden - Firebase sorgusu YOK!
  const { currentUser, userDetails } = useAuth();
  
  // Bildirim için state
  const [notification, setNotification] = useState<{message: string, isError: boolean} | null>(null);

  // Modal için state
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Bildirim gösterme fonksiyonu
  const showNotification = (message: string, isError: boolean = false) => {
    setNotification({ message, isError });
    // 3 saniye sonra bildirimi kaldır
    setTimeout(() => {
      setNotification(null);
    }, isError ? 4000 : 2000);
  };

  // LocalStorage'dan kullanıcı görünüm tercihini yükle
  useEffect(() => {
    const savedViewMode = localStorage.getItem('personnelDailyTaskViewMode');
    if (savedViewMode && (savedViewMode === 'card' || savedViewMode === 'list')) {
      setViewMode(savedViewMode as 'card' | 'list');
    }
  }, []);

  // Görünüm modu değişikliği
  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewMode: 'card' | 'list' | null
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
      // LocalStorage'a kullanıcı tercihini kaydet
      localStorage.setItem('personnelDailyTaskViewMode', newViewMode);
    }
  };

  // Sayfa değişikliği
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişikliği
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Görev durumuna göre renk döndürme
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return '#FFA000'; // Turuncu
      case 'accepted':
        return '#1976D2'; // Mavi
      case 'started':
        return '#43A047'; // Yeşil
      case 'completed':
        return '#6A1B9A'; // Mor
      default:
        return '#757575'; // Gri
    }
  };

  // Görev durumuna göre ikon döndürme
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <PendingIcon />;
      case 'accepted':
        return <TaskAltIcon />;
      case 'started':
        return <PlayCircleFilledIcon />;
      case 'completed':
        return <CheckCircleIcon />;
      default:
        return <ErrorOutlineIcon />;
    }
  };

  // Görev durumuna göre etiket döndürme
  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'accepted':
        return 'Kabul Edildi';
      case 'started':
        return 'Başlatıldı';
      case 'completed':
        return 'Tamamlandı';
      default:
        return 'Bilinmiyor';
    }
  };

  // Firebase'den görevleri yükleme (TEK SEFERLİK)
  useEffect(() => {
    const loadTasks = async () => {
      if (!currentUser) {
        setError('Kullanıcı oturumu bulunamadı');
        setLoading(false);
        return;
      }

      // ✅ companyId AuthContext cache'inden - Firebase sorgusu YOK!
      const companyId = userDetails?.companyId;
      
      if (!companyId) {
        setError('Kullanıcı bir şirkete ait değil');
        setLoading(false);
        return;
      }

      try {
        const parseImgs = (raw?: string | null): string[] => {
          if (!raw) return [];
          try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
        };
        const toMs = (iso?: string | null): number => (iso ? new Date(iso).getTime() : Date.now());

        // Kullanıcıya atanmış 'daily' (tekrarlı) görevler — SQL (taskDataService)
        const all: any[] = [];
        let page = 1;
        for (;;) {
          const res = await taskDataService.listTasks({
            taskType: 'daily',
            personnelId: currentUser.uid,
            page,
            pageSize: 200,
          });
          all.push(...res.items);
          if (res.items.length === 0 || all.length >= (res.totalCount || 0)) break;
          page++;
        }

        const tasksList: Task[] = all
          .filter((tk) => tk.isRecurring === true)
          .map((tk) => ({
            id: String(tk.id),
            name: tk.name || 'İsimsiz Görev',
            description: tk.description || '',
            status: tk.status || 'pending',
            completionType: tk.completionType || 'button',
            createdAt: toMs(tk.createdAt),
            deadline: tk.deadline || undefined,
            isRecurring: tk.isRecurring || false,
            dailyRepetitions: tk.dailyRepetitions || 1,
            repetitionTimes: parseImgs(tk.repetitionTimes),
            startTolerance: tk.startTolerance || 15,
          } as Task));

        tasksList.sort((a, b) => b.createdAt - a.createdAt);
        setTasks(tasksList);
      } catch (error) {
        console.error('Görevler yüklenirken hata:', error);
        setError('Görevler yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    loadTasks();
  }, [currentUser, userDetails?.companyId]);

  // Görev kabul etme işlemi — SQL (changeTaskStatus)
  const handleAcceptTask = async (taskId: string) => {
    if (!currentUser) return;

    try {
      await taskDataService.changeTaskStatus(Number(taskId), {
        action: 'accept',
        byUid: currentUser.uid,
      });

      showNotification('Görev başarıyla kabul edildi', false);
      
      // Görevi hemen güncelleyelim (Firebase dinleyicisi güncellemeden önce)
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { ...task, status: 'accepted' } 
            : task
        )
      );
    } catch (error) {
      console.error('Görev kabul edilirken hata:', error);
      showNotification('Görev kabul edilirken bir hata oluştu', true);
    }
  };

  // Tarih formatı
  const formatDate = (dateString: string | number): string => {
    const date = typeof dateString === 'number' 
      ? new Date(dateString) 
      : parseISO(dateString);
      
    return format(date, 'dd MMMM yyyy HH:mm', { locale: tr });
  };

  // Görev ayrıntıları modalını açma fonksiyonu
  const handleOpenDetailsModal = (task: Task) => {
    setSelectedTask(task);
    setIsDetailsModalOpen(true);
  };

  // Görev ayrıntıları modalını kapatma fonksiyonu
  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedTask(null);
  };

  const getCurrentLocation = async (): Promise<{ latitude: number; longitude: number; accuracy: number; timestamp: number } | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.log('Geolocation is not supported by your browser.');
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
          });
        },
        (error) => {
          console.log('Unable to retrieve your location:', error.message);
          resolve(null);
        },
        {
          timeout: 5000, // 5 seconds
          enableHighAccuracy: true,
        }
      );
    });
  };

  const getDeviceInfo = () => {
    return {
      platform: 'web',
      userAgent: navigator.userAgent,
    };
  };
  
  // Tekrarlı görev başlatma işlemi — SQL (startCompletion)
  const handleStartRecurringTask = async (taskId: string, timeString: string) => {
    if (!currentUser) return;

    try {
      const today = new Date();
      const dateKey = today.toISOString().split('T')[0];
      const task = tasks.find(t => t.id === taskId);

      await taskDataService.startCompletion(Number(taskId), {
        taskTime: timeString,
        taskDate: dateKey,
        personnelFirebaseUid: currentUser.uid,
        personnelFirstName: userDetails?.firstName || '',
        personnelLastName: userDetails?.lastName || '',
        startTolerance: task?.startTolerance ?? null,
      });

      showNotification('Görev başarıyla başlatıldı', false);
      setTimeSlotStatuses(prev => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          [timeString]: 'started'
        }
      }));
    } catch (error) {
      console.error('Görev başlatılırken kritik hata:', error);
      showNotification('Görev başlatılırken bir hata oluştu', true);
    }
  };

  // Tekrarlı görev tamamlama işlemi — SQL (startCompletion + completeCompletion)
  const handleCompleteRecurringTask = async (taskId: string, timeString: string) => {
    if (!currentUser) return;

    try {
      const today = new Date();
      const dateKey = today.toISOString().split('T')[0];
      const task = tasks.find(t => t.id === taskId);

      const locationData = await getCurrentLocation();
      const deviceInfo = getDeviceInfo();

      // Bugün bu saat için var olan tamamlama kaydını bul, yoksa başlat
      const existing = await taskDataService.getCompletions(Number(taskId), dateKey);
      let completionId = existing.find(c => c.taskTime === timeString)?.id;

      if (!completionId) {
        const started = await taskDataService.startCompletion(Number(taskId), {
          taskTime: timeString,
          taskDate: dateKey,
          personnelFirebaseUid: currentUser.uid,
          personnelFirstName: userDetails?.firstName || '',
          personnelLastName: userDetails?.lastName || '',
          startTolerance: task?.startTolerance ?? null,
        });
        completionId = started.id;
      }

      await taskDataService.completeCompletion(completionId, {
        personnelFirebaseUid: currentUser.uid,
        personnelFirstName: userDetails?.firstName || '',
        personnelLastName: userDetails?.lastName || '',
        locationLat: locationData?.latitude ?? null,
        locationLng: locationData?.longitude ?? null,
        locationAccuracy: locationData?.accuracy ?? null,
        devicePlatform: deviceInfo.platform,
        deviceUserAgent: deviceInfo.userAgent,
      });

      showNotification('Görev başarıyla tamamlandı', false);
      
      // State'i anında güncelle
      setTimeSlotStatuses(prev => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          [timeString]: 'completed'
        }
      }));

    } catch (error) {
      console.error('Görev tamamlanırken kritik hata:', error);
      showNotification('Görev tamamlanırken bir hata oluştu', true);
    }
  };

  // Görevin o andaki saati için durumunu kontrol et
  const getTaskTimeStatus = (task: Task, timeString: string) => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const currentTime = now.toTimeString().slice(0, 5); // HH:MM
    
    // Tolerans süresi (varsayılan 15 dakika)
    const tolerance = (task.startTolerance || 15) * 60 * 1000; // milisaniye
    const taskTime = new Date(`${today}T${timeString}:00`);
    const timeDiff = now.getTime() - taskTime.getTime();
    
    // Bir sonraki görev zamanını bul
    const taskTimes = task.repetitionTimes || [];
    const currentTimeIndex = taskTimes.indexOf(timeString);
    let completionDeadline: Date;
    
    if (currentTimeIndex !== -1 && currentTimeIndex < taskTimes.length - 1) {
      // Bir sonraki görev var - o zamana kadar tamamlanabilir
      const nextTaskTime = taskTimes[currentTimeIndex + 1];
      completionDeadline = new Date(`${today}T${nextTaskTime}:00`);
    } else {
      // Son görev veya tek görev - 1 saat sonraya kadar tamamlanabilir
      completionDeadline = new Date(taskTime.getTime() + (60 * 60 * 1000)); // 1 saat ekle
    }
    
    if (timeDiff < -tolerance) {
      return 'not-ready'; // Henüz zamanı gelmedi (tolerans öncesi)
    } else if (timeDiff >= -tolerance && now < completionDeadline) {
      return 'ready'; // Başlatılabilir ve tamamlanabilir
    } else {
      return 'missed'; // Süre geçti
    }
  };

  // Görevin tamamlanıp tamamlanmadığını kontrol et
  const [timeSlotStatuses, setTimeSlotStatuses] = useState<{ [taskId: string]: { [time: string]: 'started' | 'completed' } }>({});

  // Tamamlanan görev zamanlarını yükle
  useEffect(() => {
    if (!currentUser || tasks.length === 0) return;

    // ✅ companyId AuthContext cache'inden - Firebase sorgusu YOK!
    const companyId = userDetails?.companyId;
    if (!companyId) return;

    const loadTimeSlotStatuses = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const newStatuses: { [taskId: string]: { [time: string]: 'started' | 'completed' } } = {};

        for (const task of tasks) {
          // SQL'den bugünkü tamamlama kayıtlarını çek (taskDataService)
          const completions = await taskDataService.getCompletions(Number(task.id), today);
          const statuses: { [time: string]: 'started' | 'completed' } = {};
          completions.forEach((c) => {
            if ((c.status === 'started' || c.status === 'completed') && c.taskTime) {
              statuses[c.taskTime] = c.status;
            }
          });
          newStatuses[task.id] = statuses;
        }

        setTimeSlotStatuses(newStatuses);
      } catch (error) {
        console.error('Tamamlanan görev zamanları yüklenirken hata:', error);
      }
    };

    loadTimeSlotStatuses();
  }, [currentUser, tasks, userDetails?.companyId]);

  const getNextActionForTask = (task: Task): { action: 'start' | 'complete' | 'details', time?: string } => {
    if (task.completionType !== 'button' || task.status !== 'accepted') {
      return { action: 'details' };
    }

    const taskStatuses = timeSlotStatuses[task.id] || {};
    const repetitionTimes = task.repetitionTimes || [];

    // Önce tamamlanmayı bekleyen, başlatılmış bir görev var mı diye kontrol et
    const startedButNotCompleted = repetitionTimes.find(time =>
      taskStatuses[time] === 'started'
    );

    if (startedButNotCompleted) {
      const timeStatus = getTaskTimeStatus(task, startedButNotCompleted);
      if (timeStatus === 'ready') {
        return { action: 'complete', time: startedButNotCompleted };
      }
    }

    // Başlatılabilecek sıradaki ilk görevi bul
    const nextToStart = repetitionTimes.find(time => {
      const timeStatus = getTaskTimeStatus(task, time);
      const isStartedOrCompleted = taskStatuses[time];
      return timeStatus === 'ready' && !isStartedOrCompleted;
    });

    if (nextToStart) {
      return { action: 'start', time: nextToStart };
    }

    return { action: 'details' };
  };

  // Kart görünümü
  const renderCardView = () => {
    return (
      <Grid container spacing={2}>
        {tasks.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">
                Atanmış günlük tekrarlı görev bulunmuyor.
              </Typography>
            </Paper>
          </Grid>
        ) : (
          tasks
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((task) => (
              <Grid item xs={12} sm={6} md={4} key={task.id}>
                <TaskCard>
                  <TaskCardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="h6" component="div" fontWeight="bold" gutterBottom>
                        {task.name}
                      </Typography>
                      <StatusChip
                        icon={getStatusIcon(task.status)}
                        label={getStatusLabel(task.status)}
                        customcolor={getStatusColor(task.status)}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                      {task.description || 'Açıklama bulunmuyor.'}
                    </Typography>
                    <Box display="flex" alignItems="center" mb={1}>
                      <AccessTimeIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(task.createdAt)}
                      </Typography>
                    </Box>
                    {task.repetitionTimes && task.repetitionTimes.length > 0 && (
                      <Box display="flex" flexDirection="column" mb={1}>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                          Tekrarlanma Zamanları:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {task.repetitionTimes.map((time, index) => {
                            const timeStatus = getTaskTimeStatus(task, time);
                            const isCompleted = timeSlotStatuses[task.id]?.[time] === 'completed';
                            
                            // Buton tamamlama tipi olan görevler için butonlar göster
                            if (task.completionType === 'button' && task.status === 'accepted' && timeStatus === 'ready' && !isCompleted) {
                              return (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <Chip
                                    label={time}
                                    size="small"
                                    variant="outlined"
                                    sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}
                                  />
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    startIcon={<PlayArrowIcon />}
                                    onClick={() => handleStartRecurringTask(task.id, time)}
                                    sx={{ minWidth: 'auto', px: 1 }}
                                  >
                                    Başlat
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={<StopIcon />}
                                    onClick={() => handleCompleteRecurringTask(task.id, time)}
                                    sx={{ minWidth: 'auto', px: 1 }}
                                  >
                                    Tamamla
                                  </Button>
                                </Box>
                              );
                            } else {
                              return (
                                <Chip
                                  key={index}
                                  label={time}
                                  size="small"
                                  variant="outlined"
                                  color={isCompleted ? 'success' : 
                                         timeStatus === 'missed' ? 'error' : 
                                         timeStatus === 'ready' ? 'primary' : 'default'}
                                  sx={{ 
                                    bgcolor: isCompleted ? 'rgba(76, 175, 80, 0.08)' : 
                                            timeStatus === 'missed' ? 'rgba(244, 67, 54, 0.08)' : 
                                            timeStatus === 'ready' ? 'rgba(25, 118, 210, 0.08)' : 
                                            'rgba(158, 158, 158, 0.08)'
                                  }}
                                />
                              );
                            }
                          })}
                        </Box>
                      </Box>
                    )}
                  </TaskCardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                    {renderTaskActions(task)}
                  </CardActions>
                </TaskCard>
              </Grid>
            ))
        )}
      </Grid>
    );
  };

  // Tablo görünümü
  const renderTableView = () => {
    return (
      <TableContainer component={Paper} sx={{ 
        mt: 2, 
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        borderRadius: 2,
        overflow: 'auto',
        maxHeight: '60vh',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        },
      }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Görev Adı</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Açıklama</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Durum</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Oluşturulma Tarihi</TableCell>
              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Tekrarlanma Zamanları</TableCell>
              <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Atanmış günlük tekrarlı görev bulunmuyor.
                </TableCell>
              </TableRow>
            ) : (
              tasks
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((task) => (
                  <TableRow key={task.id}>
                    <TableCell>{task.name}</TableCell>
                    <TableCell>
                      {task.description.length > 50
                        ? `${task.description.substring(0, 50)}...`
                        : task.description || 'Açıklama bulunmuyor.'}
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        icon={getStatusIcon(task.status)}
                        label={getStatusLabel(task.status)}
                        customcolor={getStatusColor(task.status)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(task.createdAt)}</TableCell>
                    <TableCell>
                      {task.repetitionTimes && task.repetitionTimes.length > 0 ? (
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {task.repetitionTimes.slice(0, 3).map((time, index) => (
                            <Chip
                              key={index}
                              label={time}
                              size="small"
                              variant="outlined"
                              sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}
                            />
                          ))}
                          {task.repetitionTimes.length > 3 && (
                            <Chip
                              label={`+${task.repetitionTimes.length - 3}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {renderTaskActions(task)}
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Kart görünümü için task action butonlarını render et
  const renderTaskActions = (task: Task) => {
    // 1. Beklemedeki görevler için her zaman "Kabul Et" butonu gösterilir.
    if (task.status === 'pending') {
      return (
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={(e) => {
            e.stopPropagation();
            handleAcceptTask(task.id);
          }}
          sx={{ 
            minWidth: '80px', 
            whiteSpace: 'nowrap',
          }}
        >
          Kabul Et
        </Button>
      );
    }

    // 2. QR kodlu görevler için (beklemede olmayanlar) mobil uygulama butonu gösterilir.
    if (task.completionType === 'qr') {
      return (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<PhoneAndroidIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDetailsModal(task);
          }}
        >
          Mobil Uygulama Gerekli
        </Button>
      );
    }
    
    // 3. Butonla tamamlanan ve kabul edilmiş görevler için "akıllı" buton mantığı
    const { action, time } = getNextActionForTask(task);
    
    switch (action) {
      case 'start':
        return (
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<PlayArrowIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleStartRecurringTask(task.id, time!);
            }}
          >
            Başlat ({time})
          </Button>
        );
      case 'complete':
        return (
          <Button
            size="small"
            variant="contained"
            style={{ backgroundColor: '#6A1B9A', color: 'white' }}
            startIcon={<StopIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleCompleteRecurringTask(task.id, time!);
            }}
          >
            Tamamla ({time})
          </Button>
        );
      case 'details':
      default:
        // Butonlu görevlerde aktif eylem yoksa hiçbir şey gösterme
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <ViewToggleContainer>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="görünüm modu"
          size="small"
        >
          <ToggleButton value="card" aria-label="kart görünümü">
            <Tooltip title="Kart Görünümü">
              <ViewModuleIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="list" aria-label="liste görünümü">
            <Tooltip title="Liste Görünümü">
              <ViewListIcon />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </ViewToggleContainer>

      {viewMode === 'card' ? renderCardView() : renderTableView()}

      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={tasks.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Sayfa başına satır:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
      />

      {/* Bildirim Snackbar'ı */}
      {notification && (
        <Snackbar 
          open={true}
          autoHideDuration={notification.isError ? 4000 : 2000} 
          onClose={() => setNotification(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setNotification(null)} 
            severity={notification.isError ? "error" : "success"}
            sx={{
              fontWeight: 'bold',
              fontSize: '16px',
              width: '100%'
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      )}

      {/* Görev Ayrıntıları Modal'ı */}
      <Dialog
        open={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#1976D2',
          color: 'white',
          py: 2
        }}>
          <Box display="flex" alignItems="center">
            <PhoneAndroidIcon sx={{ mr: 1 }} />
            Mobil Uygulama Gerekli
          </Box>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={handleCloseDetailsModal} 
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', color: '#1976D2' }}>
            {selectedTask?.name}
          </Typography>
          {selectedTask?.completionType === 'button' ? (
            <>
              <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                Bu görev web üzerinden buton ile tamamlanabilir. Görev saatleri geldiğinde yukarıda bulunan "Başlat" ve "Tamamla" butonlarını kullanabilirsiniz.
              </Typography>
              {selectedTask.repetitionTimes && selectedTask.repetitionTimes.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Günlük Tekrar Saatleri:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                          {selectedTask.repetitionTimes.map((time, index) => {
                        const isCompleted = timeSlotStatuses[selectedTask.id]?.[time] === 'completed';
                        const timeStatus = getTaskTimeStatus(selectedTask, time);
                        return (
                          <Chip
                            key={index}
                            label={time}
                            size="small"
                            color={isCompleted ? 'success' : 
                                   timeStatus === 'missed' ? 'error' : 
                                   timeStatus === 'ready' ? 'primary' : 'default'}
                            sx={{ 
                              bgcolor: isCompleted ? 'rgba(76, 175, 80, 0.08)' : 
                                      timeStatus === 'missed' ? 'rgba(244, 67, 54, 0.08)' : 
                                      timeStatus === 'ready' ? 'rgba(25, 118, 210, 0.08)' : 
                                      'rgba(158, 158, 158, 0.08)'
                            }}
                          />
                        );
                      })}
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <>
              <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                Bu görev bir günlük tekrarlı görevdir ve sadece mobil uygulama üzerinden QR kod okutarak başlatıp bitirilebilir.
              </Typography>
              <Typography variant="body1" paragraph>
                Lütfen mobil uygulamalarımızı indiriniz:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 3, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<AndroidIcon />}
                  onClick={() => window.open('https://play.google.com/store/apps/details?id=com.mt_teknoloji.gts', '_blank')}
                  sx={{ 
                    bgcolor: '#1976D2', 
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 'bold',
                    textTransform: 'none',
                  }}
                >
                  Google Play
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AppleIcon />}
                  onClick={() => window.open('https://apps.apple.com/tr/app/gts-g%C3%B6rev-takip-sistemi/id6743622025?l=tr&platform=iphone', '_blank')}
                  sx={{ 
                    bgcolor: '#1976D2', 
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 'bold',
                    textTransform: 'none',
                  }}
                >
                  App Store
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseDetailsModal} 
            variant="outlined" 
            color="primary"
            sx={{ borderRadius: 2 }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailyTasksComponent; 